package com.budgeyet.feature.transaction.presentation

import com.budgeyet.core.cache.LocalCacheStore
import com.budgeyet.core.model.PaymentMode
import com.budgeyet.feature.category.domain.CategoryRepository
import com.budgeyet.feature.profile.domain.ProfileRepository
import com.budgeyet.feature.transaction.domain.TransactionRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

class HistoryController(
    private val transactionRepository: TransactionRepository,
    private val profileRepository: ProfileRepository,
    private val categoryRepository: CategoryRepository,
    private val cacheStore: LocalCacheStore,
    private val scope: CoroutineScope
) {
    private val _uiState = MutableStateFlow(HistoryUiState())
    val uiState: StateFlow<HistoryUiState> = _uiState.asStateFlow()

    // See DashboardController — load-once guard so a tab switch doesn't refetch.
    private var hasLoaded = false

    fun load(forceRefresh: Boolean = false) {
        if (hasLoaded && !forceRefresh) return
        hasLoaded = true
        scope.launch {
            // Cache-first paint so revisiting Transaction History renders instantly.
            if (_uiState.value.transactions.isEmpty()) {
                val cachedTransactions = cacheStore.getCachedTransactions()
                if (cachedTransactions != null) {
                    val cachedHousehold = cacheStore.getCachedHousehold()
                    _uiState.update {
                        it.copy(
                            transactions = cachedTransactions,
                            householdMembers = cachedHousehold?.members ?: it.householdMembers,
                            categories = cacheStore.getCachedCategories() ?: it.categories,
                            currency = cachedHousehold?.currency ?: it.currency
                        )
                    }
                }
            }
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            try {
                val transactions = transactionRepository.getTransactions()
                val household = profileRepository.getHousehold()
                val categories = categoryRepository.getCategories()
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        transactions = transactions,
                        householdMembers = household.members,
                        categories = categories,
                        currency = household.currency
                    )
                }
            } catch (t: Throwable) {
                _uiState.update { it.copy(isLoading = false, errorMessage = t.message ?: "Something went wrong") }
            }
        }
    }

    // Called from MainAppShell after a transaction is added/edited/deleted (or a category the
    // list renders is changed). The load-once guard means re-entering composition no longer
    // refetches, so stale data has to be invalidated explicitly.
    fun refresh() = load(forceRefresh = true)

    fun onSearchChange(query: String) = _uiState.update { it.copy(searchQuery = query) }

    fun onOpenFilterSheet() = _uiState.update { it.copy(showFilterSheet = true) }

    fun onCloseFilterSheet() = _uiState.update { it.copy(showFilterSheet = false) }

    fun onResetFilters() = _uiState.update {
        it.copy(
            dateRangeFilter = DateRangeFilter.ALL,
            customStartDate = null,
            customEndDate = null,
            selectedPayerUserIds = emptySet(),
            selectedPaymentModes = emptySet()
        )
    }

    fun onSelectDateRangeFilter(filter: DateRangeFilter) = _uiState.update { it.copy(dateRangeFilter = filter) }

    fun onTogglePayer(userId: Long) = _uiState.update {
        val current = it.selectedPayerUserIds
        it.copy(selectedPayerUserIds = if (userId in current) current - userId else current + userId)
    }

    fun onTogglePaymentMode(mode: PaymentMode) = _uiState.update {
        val current = it.selectedPaymentModes
        it.copy(selectedPaymentModes = if (mode in current) current - mode else current + mode)
    }

    fun onOpenCustomStartPicker() = _uiState.update { it.copy(showCustomStartPicker = true) }

    fun onCloseCustomStartPicker() = _uiState.update { it.copy(showCustomStartPicker = false) }

    fun onCustomStartSelected(date: LocalDate) = _uiState.update {
        it.copy(customStartDate = date, showCustomStartPicker = false, dateRangeFilter = DateRangeFilter.CUSTOM)
    }

    fun onOpenCustomEndPicker() = _uiState.update { it.copy(showCustomEndPicker = true) }

    fun onCloseCustomEndPicker() = _uiState.update { it.copy(showCustomEndPicker = false) }

    fun onCustomEndSelected(date: LocalDate) = _uiState.update {
        it.copy(customEndDate = date, showCustomEndPicker = false, dateRangeFilter = DateRangeFilter.CUSTOM)
    }
}
